import React from 'react';
import { MapPin, MessageCircle, Phone, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import siteConfig from '../config';

const Hero = () => {
  const whatsappLink = `https://wa.me/${siteConfig.contact.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(siteConfig.contact.whatsappMessage)}`;

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src={siteConfig.hero.backgroundImage}
          alt="Luxury Car Workshop"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/80 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-600/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main Title - Mobile Optimized */}
          <div className="mb-6 animate-in fade-in slide-in-from-bottom-4 duration-1000">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight leading-tight">
              {siteConfig.hero.title}
            </h1>
            <div className="h-1 w-32 bg-gradient-to-r from-transparent via-yellow-500 to-transparent mx-auto mb-6"></div>
          </div>

          {/* Subtitle - Mobile Optimized */}
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-200 mb-4 font-light animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-200 px-4">
            {siteConfig.hero.subtitle}
          </p>

          {/* Specialization - Mobile Optimized */}
          <p className="text-base sm:text-lg md:text-xl text-yellow-400 mb-8 font-medium animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300 px-4">
            {siteConfig.hero.specialization}
          </p>

          {/* Location Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-yellow-500/30 rounded-full px-6 py-3 mb-12 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-400">
            <MapPin className="w-5 h-5 text-yellow-400" />
            <span className="text-white font-medium">{siteConfig.hero.location}</span>
          </div>

          {/* CTA Buttons - Mobile Optimized */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-500 px-4 w-full max-w-md sm:max-w-none mx-auto">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold text-base sm:text-lg px-6 sm:px-8 py-6 sm:py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-yellow-500/50 group min-h-[56px]">
                <MessageCircle className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                Book on WhatsApp
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            
            <a href={`tel:${siteConfig.contact.phone}`} className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-white/10 backdrop-blur-md border-2 border-yellow-500 hover:bg-yellow-500 text-white hover:text-black font-bold text-base sm:text-lg px-6 sm:px-8 py-6 sm:py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl group min-h-[56px]">
                <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                Call Now
              </Button>
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-600">
            {siteConfig.hero.stats.map((stat, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-md border border-yellow-500/20 rounded-xl p-4 hover:bg-white/10 transition-all duration-300">
                <div className="text-3xl font-bold text-yellow-400 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-yellow-500 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-yellow-500 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
